import { Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';

import { DatasetSortField, FiltersProps, SortDirection } from 'types/Filters';
import clsx from 'clsx';
import { NIcon } from '@innersource-nn/novoui-react';

const defaultValues: SortingDropDownProps[] = [
    {
        id: 1,
        label: 'Latest updated',
        direction: 'desc',
        field: 'updated',
        selected: false,
    },
    {
        id: 2,
        label: 'Newest first',
        direction: 'desc',
        field: 'created',
        selected: false,
    },
    {
        id: 3,
        label: 'Oldest first',
        direction: 'asc',
        field: 'created',
        selected: false,
    },
    {
        id: 4,
        label: 'Alphabetical (A-Z)',
        direction: 'asc',
        field: 'label',
        selected: false,
    },
    {
        id: 5,
        label: 'Alphabetical (Z-A)',
        direction: 'desc',
        field: 'label',
        selected: false,
    },
];

interface SortingDropDownProps {
    label: string;
    direction: SortDirection;
    field: DatasetSortField;
    selected: boolean;
    id: number;
}

const SortingDropDown: React.FC<FiltersProps> = ({ setFilter }) => {
    const [SortingDropDown, setSortingDropDown] =
        useState<SortingDropDownProps[]>(defaultValues);

    const onFilterClick = (sortFilter: SortingDropDownProps) => {
        setFilter((prevFilter) => ({
            ...prevFilter,
            sort: sortFilter.selected
                ? []
                : [
                      {
                          field: sortFilter.field,
                          direction: sortFilter.direction,
                      },
                  ],
        }));

        setSortingDropDown((prevFilters) => {
            return prevFilters.map((filter) => {
                if (filter.id === sortFilter.id) {
                    return { ...filter, selected: !sortFilter.selected };
                } else {
                    return { ...filter, selected: false };
                }
            });
        });
    };

    return (
        <Listbox>
            {({ open }) => (
                <div className="w-60">
                    <div className="relative mt-2">
                        <Listbox.Button
                            className={clsx(
                                open
                                    ? 'border-sea-blue-600'
                                    : 'border-faded-blue-400',
                                'flex justify-between items-center w-full cursor-pinter bg-white rounded-md py-8 px-16 text-left text-faded-blue-600 shadow-sm border-1 focus:outline-none  sm:text-sm sm:leading-6',
                            )}
                        >
                            {'Sort by: '}
                            {
                                SortingDropDown.find(
                                    (filter) => filter.selected,
                                )?.label
                            }
                            <NIcon
                                className="fill-faded-blue-600"
                                iconName={open ? 'chevronUp' : 'chevronDown'}
                                size="normal"
                            />
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            show={open}
                            enter="transition-enter-leave"
                            enterFrom="transition-0"
                            enterTo="transition-100"
                            leave="transition-enter-leave"
                            leaveFrom="transition-100"
                            leaveTo="transition-0"
                        >
                            <Listbox.Options className="absolute flex-col z-50  max-h-60 w-full overflow-auto rounded-md bg-white mt-4 py-8 shadow-lg">
                                <b className="text-true-blue text-md py-12 px-24">
                                    Sorting option
                                </b>
                                {SortingDropDown.map((filter) => (
                                    <Listbox.Option
                                        key={filter.id}
                                        onClick={() => onFilterClick(filter)}
                                        className={({ active }) =>
                                            clsx(
                                                active && 'bg-sea-blue-100 ',
                                                filter.selected
                                                    ? 'font-medium'
                                                    : 'font-normal',
                                                'dropdown-options',
                                            )
                                        }
                                        value={filter}
                                    >
                                        <div className="flex justify-between">
                                            <span className="block truncate">
                                                {filter.label}
                                            </span>
                                            {filter.selected && (
                                                <NIcon
                                                    iconName="tick"
                                                    size="normal"
                                                    className="fill-true-blue"
                                                />
                                            )}
                                        </div>
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </div>
            )}
        </Listbox>
    );
};

export default SortingDropDown;
